import React, { type HTMLAttributes, useState, useEffect, useRef } from 'react';
import styled, { css, type RuleSet } from 'styled-components';
import { breakpoint, sizes, fonts } from 'variables';
import memoizeOne from 'memoize-one';
import ResizeObserver from 'resize-observer-polyfill';

interface IReadMore extends HTMLAttributes<HTMLDivElement> {
    lines: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        xxl?: number;
        xxxl?: number;
    };
    readMore?: string;
    readLess?: string;
    lineHeight?: number;
    fontSize?: number;
}

const ReadMore = styled.div<IReadMore & { expanded: boolean; lineHeight: number }>`
    line-height: ${({ lineHeight }) => `${lineHeight}em`};
    overflow: hidden;

    ${({ lines, expanded, lineHeight }): RuleSet =>
        Object.entries(lines).map(
            ([breakpointKey, numberOfLines]): RuleSet => css`
                ${breakpoint.up(sizes[breakpointKey])} {
                    max-height: ${!expanded && numberOfLines ? `${numberOfLines * lineHeight}em` : 'none'};
                }
            `
        )}

    p {
        margin-bottom: ${({ lineHeight }) => `${lineHeight}em`};

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const ButtonWrapper = styled.div<{ lineHeight: number }>`
    display: flex;
    align-items: flex-end;
    height: ${({ lineHeight }) => `${lineHeight}em`};
`;

const Button = styled('button')<IReadMore>`
    background: none;
    border: 0;
    margin: 0;
    padding: 0;
    text-decoration: underline;
    font-weight: bold;

    ${({ lines }): RuleSet =>
        Object.entries(lines).map(
            ([breakpointKey, numberOfLines]): RuleSet => css`
                ${breakpoint.up(sizes[breakpointKey])} {
                    display: ${numberOfLines ? 'block' : 'none'};
                }
            `
        )}
`;

const convertSizesToLinesPerBreakpoint = memoizeOne((lines): any =>
    Object.keys(sizes)
        .filter((key): boolean => !Number.isNaN(Number(sizes[key])))
        .reduce((acc, currentBreakpointKey, index): any => {
            const valuesInAcc = Object.values(acc);
            const lastValue = valuesInAcc.length ? valuesInAcc[index - 1] : undefined;
            const lastValueThatIsNotUndefined = lastValue !== undefined ? lastValue : 0;
            const currentValue = !Number.isNaN(lines[currentBreakpointKey])
                ? lines[currentBreakpointKey]
                : lastValueThatIsNotUndefined;

            acc[currentBreakpointKey] = currentValue;

            return acc;
        }, {})
);

export function ReactReadMoreMaxLines(props: IReadMore): JSX.Element {
    const { lines, readMore, readLess, lineHeight = 20, fontSize = 13, children, ...restProps } = props;
    const calculatedLineHeight = lineHeight / fontSize;
    const [expanded, setExpanded] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const readMoreRef = useRef<HTMLDivElement>(null);
    const resizeObserver = useRef(
        new ResizeObserver((entries): void => {
            entries.forEach((entry): void => {
                const { target } = entry;
                const { scrollHeight } = target;
                const linesPerBreakpoint = convertSizesToLinesPerBreakpoint(lines);
                const currentBreakpoint = breakpoint.getNameFromWidth(window.innerWidth);
                const numberOfLines = linesPerBreakpoint[currentBreakpoint];
                const maxHeight = numberOfLines ? numberOfLines * lineHeight : 0;
                const overflow = scrollHeight > maxHeight;

                if (maxHeight && overflow) {
                    setShowButton(true);
                } else {
                    setShowButton(false);
                }
            });
        })
    );

    useEffect(() => {
        const readMoreNode = readMoreRef.current;

        if (readMoreNode) {
            resizeObserver.current.observe(readMoreNode);
        }

        return (): void => {
            if (readMoreNode) {
                resizeObserver.current.unobserve(readMoreNode);
            }
        };
    });

    function handleClick(): void {
        setExpanded(!expanded);
    }

    return (
        <>
            <ReadMore
                ref={readMoreRef}
                lines={lines}
                expanded={expanded}
                lineHeight={calculatedLineHeight}
                {...restProps}
            >
                {children}
            </ReadMore>
            {showButton && (
                <ButtonWrapper lineHeight={calculatedLineHeight}>
                    <Button type="button" lines={lines} onClick={handleClick}>
                        {!expanded ? readMore : readLess}
                    </Button>
                </ButtonWrapper>
            )}
        </>
    );
}

export default ReactReadMoreMaxLines;
